// Smart Daara - Subject Management Page
// Subjects are displayed as "Subject - Class" (e.g., "Français - CE1")
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { courseAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import { 
  Plus, Pencil, Trash2, BookOpen, Loader2, FolderOpen, UserCog, Users
} from 'lucide-react';
import { toast } from 'sonner';
import { userAPI } from '../../lib/api';

export default function SubjectManagement() {
  const { user } = useAuth();
  const { language } = useLanguage();
  
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  // Teacher assignment modal
  const [teacherModalOpen, setTeacherModalOpen] = useState(false);
  const [targetSubject, setTargetSubject] = useState(null);
  const [pickedTeacherIds, setPickedTeacherIds] = useState([]);
  // Bulk enroll loading state per subject
  const [enrolling, setEnrolling] = useState(null);
  const isTeacher = user?.role === 'teacher';
  
  const [formData, setFormData] = useState({
    name: '',
    name_fr: '',
    description: '',
    description_fr: '',
    class_id: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const promises = [
        courseAPI.listSubjects({ tenant_id: user?.tenant_id }),
        courseAPI.listClasses({ tenant_id: user?.tenant_id }),
      ];
      if (!isTeacher) {
        promises.push(userAPI.listTeachers({ tenant_id: user?.tenant_id }));
      }
      const results = await Promise.allSettled(promises);
      setSubjects(results[0].status === 'fulfilled' ? (results[0].value.data || []) : []);
      setClasses(results[1].status === 'fulfilled' ? (results[1].value.data || []) : []);
      if (!isTeacher && results[2]) {
        setTeachers(results[2].status === 'fulfilled' 
          ? (results[2].value.data?.items || results[2].value.data || []) 
          : []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoading(false);
    }
  };

  const openTeacherModal = (subject) => {
    setTargetSubject(subject);
    setPickedTeacherIds(subject.teacher_ids || (subject.teacher_id ? [subject.teacher_id] : []));
    setTeacherModalOpen(true);
  };

  const toggleTeacherPick = (tid) => {
    setPickedTeacherIds(prev => prev.includes(tid) ? prev.filter(x => x !== tid) : [...prev, tid]);
  };

  const saveTeacherAssignment = async () => {
    if (!targetSubject) return;
    try {
      await courseAPI.assignSubjectTeachers(targetSubject.id, pickedTeacherIds);
      toast.success(language === 'fr' ? 'Enseignants affectés' : 'Teachers assigned');
      setTeacherModalOpen(false);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    }
  };

  const handleEnrollClass = async (subject) => {
    if (!window.confirm(language === 'fr' 
      ? `Inscrire tous les élèves de "${subject.name_fr || subject.name}" à tous ses cours ?`
      : `Enroll all students of "${subject.name_fr || subject.name}" into every course?`)) return;
    setEnrolling(subject.id);
    try {
      const res = await courseAPI.enrollClassInSubject(subject.id);
      toast.success(language === 'fr' 
        ? `${res.data.enrolled_students} élève(s) · ${res.data.created_enrollments} inscription(s) créée(s)`
        : `${res.data.enrolled_students} student(s) · ${res.data.created_enrollments} enrollment(s) created`);
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    } finally {
      setEnrolling(null);
    }
  };

  const getClassName = (classId) => {
    const classItem = classes.find(c => c.id === classId);
    return classItem ? (classItem.name_fr || classItem.name) : '';
  };

  const getDisplayName = (subject) => {
    const subjectName = subject.name_fr || subject.name;
    const className = getClassName(subject.class_id);
    if (className) {
      return `${subjectName} - ${className}`;
    }
    return subjectName;
  };

  const openModal = (subject = null) => {
    if (subject) {
      setEditingSubject(subject);
      setFormData({
        name: subject.name || '',
        name_fr: subject.name_fr || '',
        description: subject.description || '',
        description_fr: subject.description_fr || '',
        class_id: subject.class_id || ''
      });
    } else {
      setEditingSubject(null);
      setFormData({
        name: '',
        name_fr: '',
        description: '',
        description_fr: '',
        class_id: ''
      });
    }
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name_fr) {
      toast.error(language === 'fr' ? 'Le nom est requis' : 'Name is required');
      return;
    }

    if (!formData.class_id) {
      toast.error(language === 'fr' ? 'Veuillez sélectionner une classe' : 'Please select a class');
      return;
    }

    setSaving(true);
    try {
      if (editingSubject) {
        await courseAPI.updateSubject(editingSubject.id, formData);
        toast.success(language === 'fr' ? 'Matière mise à jour!' : 'Subject updated!');
      } else {
        await courseAPI.createSubject({
          ...formData,
          tenant_id: user?.tenant_id
        });
        toast.success(language === 'fr' ? 'Matière créée!' : 'Subject created!');
      }
      setModalOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving subject:', error);
      toast.error(language === 'fr' ? 'Erreur de sauvegarde' : 'Save error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (subject) => {
    if (!window.confirm(language === 'fr' 
      ? 'Êtes-vous sûr de vouloir supprimer cette matière?' 
      : 'Are you sure you want to delete this subject?')) {
      return;
    }

    try {
      await courseAPI.deleteSubject(subject.id);
      toast.success(language === 'fr' ? 'Matière supprimée!' : 'Subject deleted!');
      loadData();
    } catch (error) {
      console.error('Error deleting subject:', error);
      toast.error(language === 'fr' ? 'Erreur de suppression' : 'Delete error');
    }
  };

  // Group subjects by class
  const subjectsByClass = subjects.reduce((acc, subject) => {
    const className = getClassName(subject.class_id) || (language === 'fr' ? 'Sans classe' : 'No class');
    if (!acc[className]) {
      acc[className] = [];
    }
    acc[className].push(subject);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="subject-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-slate-900">
            {language === 'fr' ? 'Gestion des Matières' : 'Subject Management'}
          </h1>
          <p className="text-slate-500">
            {language === 'fr' 
              ? 'Les matières sont affichées au format: Matière - Classe' 
              : 'Subjects are displayed as: Subject - Class'}
          </p>
        </div>
        <Button 
          className="bg-emerald-700 hover:bg-emerald-800"
          onClick={() => openModal()}
          data-testid="add-subject-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {language === 'fr' ? 'Nouvelle Matière' : 'New Subject'}
        </Button>
      </div>

      {/* Warning if no classes */}
      {classes.length === 0 && (
        <Card className="border-0 shadow-sm bg-amber-50">
          <CardContent className="py-4">
            <p className="text-amber-800 text-sm">
              ⚠️ {language === 'fr' 
                ? 'Vous devez d\'abord créer des classes avant de créer des matières.' 
                : 'You must create classes first before creating subjects.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Subjects grouped by class */}
      {subjects.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-12 text-center">
            <BookOpen className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              {language === 'fr' ? 'Aucune matière configurée' : 'No subjects configured'}
            </h3>
            <p className="text-slate-500 mb-4">
              {language === 'fr' 
                ? 'Commencez par créer vos matières (Français, Maths, Coran...)' 
                : 'Start by creating your subjects (French, Math, Quran...)'}
            </p>
            {classes.length > 0 && (
              <Button onClick={() => openModal()}>
                <Plus className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Créer une matière' : 'Create a subject'}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(subjectsByClass).map(([className, classSubjects]) => (
            <Card key={className} className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="font-outfit text-lg flex items-center gap-2">
                  <FolderOpen className="w-5 h-5 text-emerald-600" />
                  {className}
                </CardTitle>
                <CardDescription>
                  {classSubjects.length} {language === 'fr' ? 'matière(s)' : 'subject(s)'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {classSubjects.map((subject, index) => (
                    <div 
                      key={subject.id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      data-testid={`subject-card-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <BookOpen className="w-5 h-5 text-emerald-700" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">
                            {getDisplayName(subject)}
                          </p>
                          {subject.description_fr && (
                            <p className="text-sm text-slate-500 line-clamp-1">
                              {subject.description_fr}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          title={language === 'fr' ? 'Enseignants' : 'Teachers'}
                          onClick={() => openTeacherModal(subject)}
                          data-testid={`assign-teachers-${index}`}
                        >
                          <UserCog className="w-4 h-4 text-blue-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title={language === 'fr' ? 'Inscrire la classe' : 'Enroll class'}
                          onClick={() => handleEnrollClass(subject)}
                          disabled={enrolling === subject.id}
                          data-testid={`enroll-class-${index}`}
                        >
                          {enrolling === subject.id 
                            ? <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                            : <Users className="w-4 h-4 text-emerald-600" />}
                        </Button>
                        {!isTeacher && (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => openModal(subject)}>
                              <Pencil className="w-4 h-4 text-slate-600" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(subject)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingSubject 
                ? (language === 'fr' ? 'Modifier la Matière' : 'Edit Subject')
                : (language === 'fr' ? 'Nouvelle Matière' : 'New Subject')
              }
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Classe' : 'Class'} *</Label>
              <Select 
                value={formData.class_id} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, class_id: value }))}
              >
                <SelectTrigger data-testid="subject-class">
                  <SelectValue placeholder={language === 'fr' ? 'Sélectionner une classe' : 'Select a class'} />
                </SelectTrigger>
                <SelectContent>
                  {classes.map(classItem => (
                    <SelectItem key={classItem.id} value={classItem.id}>
                      {classItem.name_fr || classItem.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                {language === 'fr' 
                  ? 'La matière sera affichée: "Nom - Classe"'
                  : 'Subject will be displayed as: "Name - Class"'
                }
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Nom (Français)' : 'Name (French)'} *</Label>
                <Input
                  value={formData.name_fr}
                  onChange={(e) => setFormData(prev => ({ ...prev, name_fr: e.target.value }))}
                  placeholder="Ex: Français, Maths, Coran..."
                  data-testid="subject-name-fr"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Nom (Anglais)' : 'Name (English)'}</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: French, Math, Quran..."
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Description' : 'Description'}</Label>
              <Textarea
                value={formData.description_fr}
                onChange={(e) => setFormData(prev => ({ ...prev, description_fr: e.target.value }))}
                placeholder={language === 'fr' ? 'Description de la matière...' : 'Subject description...'}
                rows={2}
              />
            </div>

            {/* Preview */}
            {formData.name_fr && formData.class_id && (
              <div className="p-3 bg-emerald-50 rounded-lg">
                <p className="text-sm text-emerald-800">
                  <strong>{language === 'fr' ? 'Aperçu:' : 'Preview:'}</strong>{' '}
                  {formData.name_fr} - {getClassName(formData.class_id)}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving || classes.length === 0}
              className="bg-emerald-700 hover:bg-emerald-800"
              data-testid="save-subject-btn"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Enregistrer' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Teacher Assignment Modal */}
      <Dialog open={teacherModalOpen} onOpenChange={setTeacherModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {language === 'fr' ? 'Affecter des enseignants' : 'Assign teachers'}
              {targetSubject && (
                <span className="block text-sm font-normal text-slate-500 mt-1">
                  {targetSubject.name_fr || targetSubject.name}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="max-h-96 overflow-y-auto space-y-1 py-2">
            {teachers.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">
                {language === 'fr' ? 'Aucun enseignant disponible' : 'No teacher available'}
              </p>
            ) : (
              teachers.map((t) => (
                <label
                  key={t.id}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer"
                  data-testid={`teacher-row-${t.id}`}
                >
                  <input
                    type="checkbox"
                    checked={pickedTeacherIds.includes(t.id)}
                    onChange={() => toggleTeacherPick(t.id)}
                    className="w-4 h-4 accent-emerald-600"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-800">
                      {t.first_name} {t.last_name}
                    </p>
                    <p className="text-xs text-slate-500">{t.email}</p>
                  </div>
                </label>
              ))
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTeacherModalOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={saveTeacherAssignment}
              className="bg-emerald-700 hover:bg-emerald-800"
              data-testid="save-teacher-assign"
            >
              {language === 'fr' ? `Enregistrer (${pickedTeacherIds.length})` : `Save (${pickedTeacherIds.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
